import { Focus } from '@studio/models/control/focus';
import type { T_Maybe } from '@tryiris-ai/js-types';
import { isNotNil } from '@tryiris-ai/js-types';
import type { Handler } from './types.js';

/** @internal */
export class Choreographer {
  /** Focus operations. */
  readonly #focus = new Set<Focus>();

  /** Handler callback */
  readonly #handler: Handler;

  /** `window.setInterval` identifier. */
  #intervalId: T_Maybe<number>;

  /**
   * Manages focus operation.
   *
   * @param handler - The function to be called in response to a focus request.
   */
  public constructor(handler: Handler) {
    this.#handler = handler;
  }

  /**
   * Add the specified focus operation to the choreography.
   *
   * @param focus - Focus operation to be added.
   */
  public add(focus: Focus): void {
    this.#focus.add(focus);

    this.#sync();
  }

  /**
   * Remove the specified focus operation from the choreography.
   *
   * @param focus - Focus operation to be removed.
   */
  public remove(focus: Focus): void {
    this.#focus.delete(focus);

    this.#sync();
  }

  /** Stop all focus operations. */
  public stop(): void {
    this.#focus.clear();

    this.#sync();
  }

  /** Synchronize interval state. */
  #sync(): void {
    const isPumping = isNotNil(this.#intervalId);
    const shouldPump = this.#focus.size > 0;

    // Has something changed?
    if (isPumping !== shouldPump) {
      // Start or stop?
      if (shouldPump) {
        // Start pumping
        this.#intervalId = window.setInterval(this.#tick, 250);
      } else {
        // Stop pumping
        window.clearInterval(this.#intervalId);
        this.#intervalId = undefined;
      }

      // Tick on the leading edge of interval
      this.#tick();
    }
  }

  /** @internal */
  #tick = (): void => {
    const bNear = this.#focus.has(Focus.Near);
    const bFar = this.#focus.has(Focus.Far);

    if ((bNear && bFar) || (!bNear && !bFar)) {
      this.#handler(false);
    } else if (bNear) {
      this.#handler(Focus.Near);
    } else if (bFar) {
      this.#handler(Focus.Far);
    }
  };
}
